import { API_URL } from 'config';
import React, { useState } from 'react';
import { useLanguage } from '../../../context/LanguageContext';

export const PaymentModal = ({ modalData, onClose, onSelectLicense, onUpgradeToPermanent, onConfirmModulePay, prices = {} }) => {
  const licensePrices = prices.license_prices || { demo: 0, monthly: 50, permanent: 300 };
  const modulePrices = prices.modules || {};
  const { t } = useLanguage();
  const [referralCode, setReferralCode] = useState('');
  const [appliedCode, setAppliedCode] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [validating, setValidating] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [validatingPromo, setValidatingPromo] = useState(false);
  const [promoValidationMessage, setPromoValidationMessage] = useState('');

  if (!modalData.isOpen) return null;

  const isLicense = modalData.type === 'license';
  const isUpgrade = modalData.data?.mode === 'upgrade';
  const preselectedType = modalData.data?.preselectedType;

  const parseModulePrice = (price) => {
    if (typeof price === 'number') return price;
    if (typeof price === 'string') {
      const cleaned = price.replace(/[^0-9.]/g, '');
      return parseFloat(cleaned) || 0;
    }
    return 0;
  };

  const modulePrice = parseModulePrice(
    modulePrices[modalData.data?.id]?.price ?? modalData.data?.price
  );

  const applyReferralCode = async () => {
    if (!referralCode.trim()) return;
    setValidating(true);
    setValidationMessage('');
    try {
      const response = await fetch(`${API_URL}/license/validate-referral-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ referral_code: referralCode.trim() })
      });
      const data = await response.json();
      if (response.status === 200 && data.status === 'valid' && (data.discount_percent || 0) > 0) {
        setAppliedCode(referralCode.trim().toUpperCase());
        setDiscount((data.discount_percent || 0) / 100);
        setValidationMessage(t('dashboard.referralCodeApplied', { percent: data.discount_percent }));
      } else {
        setAppliedCode(null);
        setDiscount(0);
        setValidationMessage(t('dashboard.referralCodeInvalid'));
      }
    } catch (error) {
      setAppliedCode(null);
      setDiscount(0);
      setValidationMessage(t('dashboard.referralCodeInvalid'));
    } finally {
      setValidating(false);
    }
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) return;
    setValidatingPromo(true);
    setPromoValidationMessage('');
    try {
      const response = await fetch(`${API_URL}/license/validate-promo-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ promo_code: promoCode.trim() })
      });
      const data = await response.json();
      if (response.status === 200 && data.status === 'valid' && (data.discount_percent || 0) > 0) {
        setAppliedPromo(promoCode.trim().toUpperCase());
        setPromoDiscount((data.discount_percent || 0) / 100);
        setPromoValidationMessage(t('dashboard.promoCodeApplied', { percent: data.discount_percent }));
      } else {
        setAppliedPromo(null);
        setPromoDiscount(0);
        setPromoValidationMessage(t('dashboard.promoCodeInvalid'));
      }
    } catch (error) {
      setAppliedPromo(null);
      setPromoDiscount(0);
      setPromoValidationMessage(t('dashboard.promoCodeInvalid'));
    } finally {
      setValidatingPromo(false);
    }
  };

  const discountedPrice = (price) => {
    if (!price) return price;
    const multiplier = (1 - (discount || 0)) * (1 - (promoDiscount || 0));
    return Math.round(price * multiplier * 100) / 100;
  };

  const handleLicenseSelect = (type) => {
    if (isUpgrade) {
      onUpgradeToPermanent({ referralCode: appliedCode, promoCode: appliedPromo });
    } else {
      onSelectLicense({ type, referralCode: appliedCode, promoCode: appliedPromo });
    }
  };

  const handleModulePay = () => {
    if (modalData.data?.id) {
      onConfirmModulePay({ moduleId: modalData.data.id, referralCode: appliedCode, promoCode: appliedPromo });
    }
  };

  const renderReferralBlock = () => (
    <div className="payment-referral-block">
      <div className="payment-referral-header">
        <i className="fas fa-tag"></i>
        <span>{t('dashboard.applyReferralCode')}</span>
      </div>
      <div className="payment-referral-input-row">
        <input
          type="text"
          className="referral-code-input"
          placeholder={t('dashboard.referralCodePlaceholder') || 'XXXX-XXXX'}
          value={referralCode}
          onChange={(e) => {
            setReferralCode(e.target.value.toUpperCase());
            setAppliedCode(null);
            setDiscount(0);
            setValidationMessage('');
          }}
          onKeyDown={(e) => e.key === 'Enter' && applyReferralCode()}
          maxLength={16}
        />
        <button
          className="cyber-action-btn payment-referral-apply-btn"
          onClick={applyReferralCode}
          disabled={validating || !referralCode.trim()}
        >
          {validating ? '...' : t('dashboard.apply')}
        </button>
      </div>
      {validationMessage && (
        <p className={`payment-referral-message ${appliedCode ? 'success' : 'error'}`}>
          {validationMessage}
        </p>
      )}
      <p className="payment-referral-hint">
        {t('dashboard.referralHint')}
      </p>
    </div>
  );

  const renderPromoBlock = () => (
    <div className="payment-referral-block">
      <div className="payment-referral-header">
        <i className="fas fa-ticket-alt"></i>
        <span>{t('dashboard.applyPromoCode')}</span>
      </div>
      <div className="payment-referral-input-row">
        <input
          type="text"
          className="referral-code-input"
          placeholder={t('dashboard.promoCodePlaceholder') || 'PROMO20'}
          value={promoCode}
          onChange={(e) => {
            setPromoCode(e.target.value.toUpperCase());
            setAppliedPromo(null);
            setPromoDiscount(0);
            setPromoValidationMessage('');
          }}
          onKeyDown={(e) => e.key === 'Enter' && applyPromoCode()}
          maxLength={32}
        />
        <button
          className="cyber-action-btn payment-referral-apply-btn"
          onClick={applyPromoCode}
          disabled={validatingPromo || !promoCode.trim()}
        >
          {validatingPromo ? '...' : t('dashboard.apply')}
        </button>
      </div>
      {promoValidationMessage && (
        <p className={`payment-referral-message ${appliedPromo ? 'success' : 'error'}`}>
          {promoValidationMessage}
        </p>
      )}
      <p className="payment-referral-hint">
        {t('dashboard.promoHint')}
      </p>
    </div>
  );

  const renderLicenseOptions = () => {
    const available = licensePrices || {};
    const allOptions = [];
    if (available.demo === 0) {
      allOptions.push({
        type: 'demo',
        title: 'DEMO',
        price: 0,
        label: t('dashboard.free'),
        period: t('dashboard.demoPeriod'),
        color: '#3b82f6',
        button: isUpgrade ? (t('dashboard.upgrade') || 'Upgrade') : t('dashboard.select'),
      });
    }
    if (typeof available.monthly === 'number') {
      allOptions.push({
        type: 'monthly',
        title: 'MONTHLY',
        price: available.monthly,
        period: t('dashboard.monthlyPeriod'),
        color: '#8b5cf6',
        button: isUpgrade ? (t('dashboard.upgrade') || 'Upgrade') : t('dashboard.buy'),
      });
    }
    if (typeof available.permanent === 'number') {
      allOptions.push({
        type: 'permanent',
        title: 'PERMANENT',
        price: available.permanent,
        period: t('dashboard.permanentPeriod'),
        color: '#10b981',
        button: isUpgrade ? (t('dashboard.upgrade') || 'Upgrade') : t('dashboard.buy'),
      });
    }

    const options = preselectedType
      ? allOptions.filter((o) => o.type === preselectedType)
      : allOptions;

    if (options.length === 0) {
      return (
        <p className="payment-modal-subtitle" style={{ color: '#ef4444' }}>
          {t('dashboard.noLicenseMessage')}
        </p>
      );
    }

    return (
      <div className="license-options-grid">
        {options.map((opt) => {
          const isFree = opt.price === 0;
          const finalPrice = isFree ? 0 : discountedPrice(opt.price);
          return (
            <div key={opt.type} className="cyber-panel payment-option" style={{ borderColor: opt.color }}>
              <h5>{opt.title}</h5>
              <p className="payment-option-price">
                {!isFree && (discount > 0 || promoDiscount > 0) ? (
                  <>
                    <span className="payment-old-price">${opt.price}</span>
                    ${finalPrice}
                  </>
                ) : (
                  <>{isFree ? opt.label : `$${opt.price}`}</>
                )}
              </p>
              <span className="payment-option-period">{opt.period}</span>
              <button
                className="cyber-action-btn"
                style={{ fontSize: '12px', padding: '6px', background: opt.color, color: '#fff' }}
                onClick={() => handleLicenseSelect(opt.type)}
              >
                {opt.button}
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="payment-popup-overlay" onClick={onClose}>
      <div className="payment-card" onClick={(e) => e.stopPropagation()}>
        <h3 className="payment-modal-title">
          {isLicense && isUpgrade
            ? t('dashboard.upgradeToPermanent')
            : t('dashboard.transaction')}
        </h3>

        {isLicense ? (
          <div>
            <p className="payment-modal-subtitle">
              {preselectedType
                ? t('dashboard.selectLicensePlan')
                : t('dashboard.selectLicenseType')}
            </p>
            {renderLicenseOptions()}
            {renderReferralBlock()}
            {renderPromoBlock()}
          </div>
        ) : (
          <div>
            <p className="payment-modal-subtitle">
              {t('dashboard.selectModuleToPurchase') || t('dashboard.selectLicensePlan')}
            </p>
            <div className="cyber-panel payment-module-card" style={{ borderColor: '#8b5cf6' }}>
              <h4 className="payment-module-name">{modalData.item}</h4>
              <p className="payment-module-desc">{modalData.data?.description || ''}</p>
              <p className="cyber-pay-amount">
                {discount > 0 || promoDiscount > 0 ? (
                  <>
                    <span className="payment-old-price">${modulePrice}</span>
                    ${discountedPrice(modulePrice)}
                  </>
                ) : (
                  <>${modulePrice}</>
                )}
              </p>

              <button 
                className="cyber-action-btn"
                style={{ background: '#f59e0b', color: '#000', marginTop: '16px' }}
                onClick={handleModulePay}
              >
                <i className="fab fa-bitcoin"></i>
                {t('dashboard.payWithCrypto')}
              </button>
            </div>
            {renderReferralBlock()}
            {renderPromoBlock()}
          </div>
        )}

        <button className="payment-close-btn" onClick={onClose} aria-label="Close">
          <i className="fas fa-times"></i>
        </button>
      </div>
    </div>
  );
};
